import { growingCanvasBounds } from '@system-atlas/shared';
import type { CanvasMeta, SceneDoc } from '@system-atlas/shared';

/** Seitenformate für die Zeichenfläche (Maße in px bei ~96 dpi). */
export type CanvasFormat = NonNullable<CanvasMeta['format']>;

// 96 dpi: mm * 96 / 25.4
const A4 = { long: 1123, short: 794 }; // 297 × 210 mm
const A3 = { long: 1587, short: 1123 }; // 420 × 297 mm

export const CANVAS_FORMAT_SIZES: Record<Exclude<CanvasFormat, 'free'>, { width: number; height: number }> = {
  'a4-l': { width: A4.long, height: A4.short },
  'a4-p': { width: A4.short, height: A4.long },
  'a3-l': { width: A3.long, height: A3.short },
  'a3-p': { width: A3.short, height: A3.long },
};

export const DEFAULT_CANVAS: CanvasMeta = { width: A4.long, height: A4.short, format: 'a4-l' };

const FREE_PADDING = 200;

/**
 * Effektive Zeichenfläche inkl. Ursprung.
 * Feste Formate beginnen bei (0,0); 'free' wächst in alle Richtungen mit dem Inhalt
 * (mind. A4 quer ab Ursprung, nach links/oben bei negativen Koordinaten).
 */
export interface EffectiveCanvas {
  x: number;
  y: number;
  width: number;
  height: number;
  format?: CanvasFormat;
}

export function effectiveCanvas(scene: SceneDoc): EffectiveCanvas {
  const canvas = scene.canvas ?? DEFAULT_CANVAS;
  if (canvas.format !== 'free') {
    return { x: 0, y: 0, width: canvas.width, height: canvas.height, format: canvas.format };
  }
  const bounds = growingCanvasBounds(scene.nodes, {
    padding: FREE_PADDING,
    minWidth: DEFAULT_CANVAS.width,
    minHeight: DEFAULT_CANVAS.height,
  });
  return { ...bounds, format: 'free' };
}

/** Liefert die Canvas-Metadaten für ein gewähltes Format. */
export function canvasForFormat(format: CanvasFormat): CanvasMeta {
  if (format === 'free') return { width: DEFAULT_CANVAS.width, height: DEFAULT_CANVAS.height, format: 'free' };
  return { ...CANVAS_FORMAT_SIZES[format], format };
}

export const ZOOM_MIN = 0.2;
export const ZOOM_MAX = 4;

export function clampZoom(scale: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
}
